import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import type { FinanceOverview } from '../../shared/api/types';
import { formatMoney, parseMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';

type Key = 'misiones_in' | 'misiones_out' | 'distrito_in' | 'distrito_out' | 'ebv_in';
interface AdOfferings {
  from: string;
  to: string;
  designations: { key: Key; label: string; categoryId: string | null; categoryName: string | null }[];
  totals: { key: Key; currency: string; cents: number }[];
  pending: { destination: 'misiones' | 'distrito'; currency: string; cents: number }[];
}

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const pad = (n: number) => String(n).padStart(2, '0');

/** Asambleas de Dios · ofrendas designadas: misiones, distrito y Escuela Bíblica de Vacaciones. */
export function AdOfferingsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(null);
  const from = month ? `${year}-${pad(month)}-01` : `${year}-01-01`;
  const to = month ? `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}` : `${year}-12-31`;
  const base = `/multichurch/churches/${churchId}/ad/offerings`;
  const data = useAsync((signal) => (churchId ? api.get<AdOfferings>(`${base}?from=${from}&to=${to}`, signal) : Promise.resolve(null)), [base, from, to]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const canManage = me.can('finance.manage');

  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  const d = data.status === 'ready' ? data.data : null;
  const ready = d ? d.designations.every((x) => x.categoryId) : false;

  async function setup() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${base}/setup`);
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const amount = (key: Key) => {
    const rows = d?.totals.filter((t) => t.key === key) ?? [];
    return rows.length ? rows.map((r) => formatMoney(r.cents, r.currency)).join(' · ') : formatMoney(0, d?.totals[0]?.currency ?? 'MXN');
  };

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <p className="eyebrow">Asambleas de Dios</p>
        <h1>Ofrendas misioneras</h1>
        <p className="muted">
          Lo que la iglesia recibe para misiones, para el distrito y para la Escuela Bíblica de Vacaciones, lo que ya se envió y lo que falta por enviar. El dinero se registra en{' '}
          <Link to="/app/finanzas">Finanzas</Link> con estas categorías.
        </p>
      </header>
      {error ? <Alert>{error}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}

      {d && !ready ? (
        <Card title="Preparar las categorías">
          <p className="small">
            Para empezar, KEORA crea en Finanzas las categorías <strong>Ofrenda misionera</strong>, <strong>Envío a misiones</strong>, <strong>Ofrenda para el distrito</strong>,{' '}
            <strong>Envío al distrito</strong> y <strong>Escuela Bíblica de Vacaciones</strong>. Si ya tienes alguna con ese nombre, se usa la tuya.
          </p>
          {canManage ? (
            <Button busy={busy} onClick={() => void setup()}>
              Preparar categorías
            </Button>
          ) : (
            <p className="small muted">Lo prepara quien administra las finanzas.</p>
          )}
        </Card>
      ) : null}

      {d && ready ? (
        <>
          <div className="actions actions--wrap" role="group" aria-label="Periodo">
            <div className="field">
              <label htmlFor="off-month">Mes</label>
              <select id="off-month" value={month ?? 0} onChange={(e) => setMonth(Number(e.target.value) || null)}>
                <option value={0}>Todo el año</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="off-year">Año</label>
              <select id="off-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {Array.from({ length: 6 }, (_, i) => now.getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {sent ? <Alert tone="success">{sent}</Alert> : null}
          {d.pending.filter((p) => p.cents > 0).length ? (
            <Alert tone="warning">
              Falta por enviar:{' '}
              {d.pending
                .filter((p) => p.cents > 0)
                .map((p) => `${formatMoney(p.cents, p.currency)} ${p.destination === 'misiones' ? 'a misiones' : 'al distrito'}`)
                .join(' y ')}
              .{' '}
              {canManage && !sending ? (
                <Button variant="secondary" onClick={() => { setSent(null); setSending(true); }}>
                  Registrar envío
                </Button>
              ) : null}
            </Alert>
          ) : null}
          {sending ? (
            <SendForm
              churchId={churchId}
              offerings={d}
              onCancel={() => setSending(false)}
              onDone={(text) => {
                setSending(false);
                setSent(text);
                data.reload();
              }}
            />
          ) : null}

          <Card title={month ? `${MONTHS[month - 1]} ${year}` : `Año ${year}`}>
            <dl className="details">
              {d.designations.map((x) => (
                <div key={x.key} style={{ display: 'contents' }}>
                  <dt>{x.label}</dt>
                  <dd className="money">{amount(x.key)}</dd>
                </div>
              ))}
            </dl>
            <p className="small muted">Categorías en Finanzas: {d.designations.map((x) => x.categoryName).join(', ')}.</p>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/** Registrar en Finanzas, como gasto, lo que se envió a misiones o al distrito. */
function SendForm({ churchId, offerings, onCancel, onDone }: { churchId: string; offerings: AdOfferings; onCancel: () => void; onDone: (text: string) => void }) {
  const fin = useAsync((signal) => api.get<FinanceOverview>(`/finance/churches/${churchId}/accounts`, signal), [churchId]);
  const pending = offerings.pending.filter((p) => p.cents > 0);
  const [destination, setDestination] = useState<'misiones' | 'distrito'>(pending[0]?.destination ?? 'misiones');
  const owed = pending.find((p) => p.destination === destination);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accounts = fin.status === 'ready' ? fin.data.accounts.filter((a) => !a.archived) : [];
  const label = destination === 'misiones' ? 'Envío a misiones' : 'Envío al distrito';

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const amountCents = parseMoney(String(f.get('amount') ?? ''));
    const accountId = String(f.get('accountId') ?? '');
    const categoryId = offerings.designations.find((x) => x.key === (destination === 'misiones' ? 'misiones_out' : 'distrito_out'))?.categoryId;
    if (!amountCents) return setError('Escribe la cantidad.');
    if (!accountId) return setError('Elige la cuenta de donde salió el dinero.');
    if (!categoryId) return setError('Prepara primero las categorías.');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/finance/churches/${churchId}/movements`, { accountId, kind: 'expense', categoryId, amountCents, occurredOn: String(f.get('occurredOn')), description: label });
      onDone(`Listo: ${label.toLowerCase()} registrado en Finanzas.`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title="Registrar envío">
      <form className="form" onSubmit={save} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        {fin.status === 'error' ? <ErrorState error={fin.error} onRetry={fin.reload} /> : null}
        <div className="form__row">
          <div className="field">
            <label htmlFor="send-destination">A dónde</label>
            <select id="send-destination" value={destination} onChange={(e) => setDestination(e.target.value as 'misiones' | 'distrito')}>
              <option value="misiones">A misiones</option>
              <option value="distrito">Al distrito</option>
            </select>
          </div>
          <Field key={destination} label="Cantidad" name="amount" inputMode="decimal" required defaultValue={owed ? (owed.cents / 100).toFixed(2) : ''} />
        </div>
        <div className="form__row">
          <div className="field">
            <label htmlFor="send-account">Cuenta</label>
            <select id="send-account" name="accountId" defaultValue="" key={accounts.length}>
              {accounts.length === 1 ? null : <option value="">Elige…</option>}
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.currency})
                </option>
              ))}
            </select>
          </div>
          <Field label="Fecha" name="occurredOn" type="date" required defaultValue={fin.status === 'ready' ? fin.data.today : new Date().toISOString().slice(0, 10)} key={fin.status} />
        </div>
        <p className="small muted">Se guarda en Finanzas como gasto con la categoría «{label}».</p>
        <div className="actions actions--wrap">
          <Button type="submit" busy={busy}>
            Guardar envío
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
